import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { Objective, SuccessPlanResponse } from '../../lib/types';
import { PRIORITY_TONE, formatDate, titleCase } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  Loading,
  SectionNote,
  Select,
  Textarea,
  useToast,
} from '../../components/ui';

/**
 * §6.4 Success plan.
 *
 * These numbers are not decoration — the response SLA drives §16, the meeting and
 * business-review cadences drive §20, and the objectives are what a QBR is
 * actually about. Editing is a single PUT of the whole plan, which is why
 * objectives keep their identity across a save.
 */

interface Draft {
  objectiveSummary: string;
  responseSlaBusinessHours: string;
  meetingCadenceType: string;
  meetingCadenceDays: string;
  businessReviewType: string;
  businessReviewCadenceDays: string;
  proactiveOutreachCadenceDays: string;
  notes: string;
  objectives: Array<Omit<Objective, 'sortOrder'> & { sortOrder?: number }>;
}

export function AccountSuccessPlanTab({ accountId }: { accountId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);

  const query = useQuery({
    queryKey: ['success-plan', accountId],
    queryFn: () => api.get<SuccessPlanResponse>(`/accounts/${accountId}/success-plan`),
  });

  useEffect(() => {
    if (!query.data || draft) return;
    const plan = query.data.plan;
    setDraft({
      objectiveSummary: plan?.objectiveSummary ?? '',
      responseSlaBusinessHours: plan?.responseSlaBusinessHours?.toString() ?? '',
      meetingCadenceType: plan?.meetingCadenceType ?? 'none',
      meetingCadenceDays: plan?.meetingCadenceDays?.toString() ?? '',
      businessReviewType: plan?.businessReviewType ?? 'none',
      businessReviewCadenceDays: plan?.businessReviewCadenceDays?.toString() ?? '',
      proactiveOutreachCadenceDays: plan?.proactiveOutreachCadenceDays?.toString() ?? '',
      notes: plan?.notes ?? '',
      objectives: query.data.objectives,
    });
  }, [query.data, draft]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('No draft');
      return api.put(`/accounts/${accountId}/success-plan`, {
        objectiveSummary: draft.objectiveSummary || null,
        responseSlaBusinessHours: draft.responseSlaBusinessHours
          ? Number(draft.responseSlaBusinessHours)
          : null,
        meetingCadenceType: draft.meetingCadenceType,
        meetingCadenceDays: draft.meetingCadenceDays ? Number(draft.meetingCadenceDays) : null,
        businessReviewType: draft.businessReviewType,
        businessReviewCadenceDays: draft.businessReviewCadenceDays
          ? Number(draft.businessReviewCadenceDays)
          : null,
        proactiveOutreachCadenceDays: draft.proactiveOutreachCadenceDays
          ? Number(draft.proactiveOutreachCadenceDays)
          : null,
        notes: draft.notes || null,
        objectives: draft.objectives.map((objective) => ({
          id: objective.id.startsWith('new-') ? undefined : objective.id,
          title: objective.title,
          description: objective.description,
          status: objective.status,
          priority: objective.priority,
          targetDate: objective.targetDate,
        })),
      });
    },
    onSuccess: () => {
      toast('Success plan saved');
      setEditing(false);
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['success-plan', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['account-summary', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['cadence'] });
    },
  });

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorNotice error={query.error} onRetry={() => query.refetch()} />;
  if (!query.data || !draft) return null;

  const { plan, objectives, derived, note } = query.data;

  if (!editing) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader
              title="Objective"
              actions={
                <Button size="sm" onClick={() => setEditing(true)}>
                  {plan ? 'Edit plan' : 'Create plan'}
                </Button>
              }
            />
            {plan ? (
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-ink">
                {plan.objectiveSummary ?? 'No objective summary recorded.'}
              </p>
            ) : (
              <SectionNote>{note}</SectionNote>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Objectives"
              count={objectives.length}
              description="What success looks like, and by when."
            />
            {objectives.length === 0 ? (
              <EmptyState title="No objectives recorded" />
            ) : (
              <ul className="divide-y divide-border">
                {objectives.map((objective) => (
                  <li key={objective.id} className="py-2.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-ink">{objective.title}</p>
                        {objective.description && (
                          <p className="mt-0.5 text-xs text-ink-2">{objective.description}</p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge tone={OBJECTIVE_TONE[objective.status] ?? 'neutral'}>
                          {titleCase(objective.status)}
                        </Badge>
                        <Badge tone={PRIORITY_TONE[objective.priority]} dot={false}>
                          {titleCase(objective.priority)}
                        </Badge>
                        <span className="text-xs whitespace-nowrap text-muted">
                          {formatDate(objective.targetDate)}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <Card>
          <CardHeader title="Agreed standards" description="What the platform measures against." />
          <dl className="space-y-3">
            <Detail label="Response SLA">
              {plan?.responseSlaBusinessHours
                ? `${plan.responseSlaBusinessHours} business hours`
                : 'Not set'}
              <span className="mt-0.5 block text-xs text-muted">
                Measured on the account's working calendar, not wall-clock hours.
              </span>
            </Detail>
            <Detail label="Meeting cadence">
              {titleCase(plan?.meetingCadenceType ?? 'none')}
              {derived?.meetingIntervalDays && (
                <span className="mt-0.5 block text-xs text-muted">
                  Every {derived.meetingIntervalDays} days
                </span>
              )}
            </Detail>
            <Detail label="Business review">
              {titleCase(plan?.businessReviewType ?? 'none')}
              {derived?.businessReviewIntervalDays && (
                <span className="mt-0.5 block text-xs text-muted">
                  Every {derived.businessReviewIntervalDays} days
                </span>
              )}
            </Detail>
            <Detail label="Proactive outreach">
              {plan?.proactiveOutreachCadenceDays
                ? `Every ${plan.proactiveOutreachCadenceDays} days`
                : 'Not set'}
            </Detail>
            {plan?.notes && <Detail label="Notes">{plan.notes}</Detail>}
          </dl>
        </Card>
      </div>
    );
  }

  const update = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
      className="space-y-4"
    >
      <Card>
        <CardHeader title="Objective" />
        <Field label="Objective summary" hint="The one-paragraph statement of what success is.">
          <Textarea
            value={draft.objectiveSummary}
            onChange={(event) => update({ objectiveSummary: event.target.value })}
            rows={3}
          />
        </Field>
      </Card>

      <Card>
        <CardHeader
          title="Agreed standards"
          description="These numbers drive the SLA clock and the cadence engine."
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Response SLA (business hours)">
            <Input
              type="number"
              min={1}
              max={720}
              value={draft.responseSlaBusinessHours}
              onChange={(event) => update({ responseSlaBusinessHours: event.target.value })}
            />
          </Field>
          <Field label="Meeting cadence">
            <Select
              value={draft.meetingCadenceType}
              onChange={(event) => update({ meetingCadenceType: event.target.value })}
            >
              <option value="none">None</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="custom">Custom</option>
            </Select>
          </Field>
          {draft.meetingCadenceType === 'custom' && (
            <Field label="Meeting interval (days)" required>
              <Input
                type="number"
                min={1}
                value={draft.meetingCadenceDays}
                onChange={(event) => update({ meetingCadenceDays: event.target.value })}
              />
            </Field>
          )}
          <Field label="Business review">
            <Select
              value={draft.businessReviewType}
              onChange={(event) => update({ businessReviewType: event.target.value })}
            >
              <option value="none">None</option>
              <option value="monthly">Monthly (MBR)</option>
              <option value="quarterly">Quarterly (QBR)</option>
              <option value="custom">Custom</option>
            </Select>
          </Field>
          {draft.businessReviewType === 'custom' && (
            <Field label="Review interval (days)" required>
              <Input
                type="number"
                min={1}
                value={draft.businessReviewCadenceDays}
                onChange={(event) => update({ businessReviewCadenceDays: event.target.value })}
              />
            </Field>
          )}
          <Field label="Proactive outreach (days)">
            <Input
              type="number"
              min={1}
              value={draft.proactiveOutreachCadenceDays}
              onChange={(event) => update({ proactiveOutreachCadenceDays: event.target.value })}
            />
          </Field>
        </div>
        <div className="mt-3">
          <Field label="Notes">
            <Textarea
              value={draft.notes}
              onChange={(event) => update({ notes: event.target.value })}
              rows={2}
            />
          </Field>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Objectives"
          count={draft.objectives.length}
          actions={
            <Button
              type="button"
              size="sm"
              onClick={() =>
                update({
                  objectives: [
                    ...draft.objectives,
                    {
                      id: `new-${Date.now()}`,
                      title: '',
                      description: null,
                      status: 'not_started',
                      priority: 'medium',
                      targetDate: null,
                    },
                  ],
                })
              }
            >
              Add objective
            </Button>
          }
        />
        <div className="space-y-3">
          {draft.objectives.map((objective, index) => (
            <div key={objective.id} className="rounded-md border border-border p-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                <div className="sm:col-span-4">
                  <Field label="Title" required>
                    <Input
                      value={objective.title}
                      onChange={(event) => {
                        const next = [...draft.objectives];
                        next[index] = { ...objective, title: event.target.value };
                        update({ objectives: next });
                      }}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Description">
                    <Input
                      value={objective.description ?? ''}
                      onChange={(event) => {
                        const next = [...draft.objectives];
                        next[index] = { ...objective, description: event.target.value || null };
                        update({ objectives: next });
                      }}
                    />
                  </Field>
                </div>
                <Field label="Status">
                  <Select
                    value={objective.status}
                    onChange={(event) => {
                      const next = [...draft.objectives];
                      next[index] = { ...objective, status: event.target.value };
                      update({ objectives: next });
                    }}
                  >
                    <option value="not_started">Not started</option>
                    <option value="in_progress">In progress</option>
                    <option value="at_risk">At risk</option>
                    <option value="achieved">Achieved</option>
                    <option value="dropped">Dropped</option>
                  </Select>
                </Field>
                <Field label="Priority">
                  <Select
                    value={objective.priority}
                    onChange={(event) => {
                      const next = [...draft.objectives];
                      next[index] = {
                        ...objective,
                        priority: event.target.value as Objective['priority'],
                      };
                      update({ objectives: next });
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </Select>
                </Field>
                <Field label="Target date">
                  <Input
                    type="date"
                    value={objective.targetDate ?? ''}
                    onChange={(event) => {
                      const next = [...draft.objectives];
                      next[index] = { ...objective, targetDate: event.target.value || null };
                      update({ objectives: next });
                    }}
                  />
                </Field>
                <div className="flex items-end sm:col-span-3">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      update({
                        objectives: draft.objectives.filter((_, i) => i !== index),
                      })
                    }
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {mutation.error && <ErrorNotice error={mutation.error} />}

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setEditing(false);
            setDraft(null);
          }}
        >
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save plan'}
        </Button>
      </div>
    </form>
  );
}

const OBJECTIVE_TONE: Record<string, 'good' | 'warning' | 'serious' | 'critical' | 'neutral' | 'info'> =
  {
    not_started: 'neutral',
    in_progress: 'info',
    at_risk: 'serious',
    achieved: 'good',
    dropped: 'neutral',
  };
